import { ReactNode } from 'react';
import { Box, Stack } from '@mui/material';
import { CanvasContentId } from 'types/canvas';
import { CanvasGroupLinkButton } from 'components/canvas/groups';

export interface CanvasHostHeaderProps {
  componentId: number;
  contentId: CanvasContentId;
  /**
   * Optional extra controls rendered to the left of the group-link button
   * (e.g. a settings menu). Most thin-wrapper hosts pass nothing here — the
   * underlying chart already provides its own header below this strip.
   */
  children?: ReactNode;
}

/**
 * Tiny right-justified strip containing the symbol-group link button (and
 * any host-level extras). Used by Canvas* host wrappers whose underlying
 * chart already renders its own internal header — this just adds a slim
 * row above for the group affordance, keeping placement consistent across
 * every single-symbol component.
 */
export const CanvasHostHeader = ({
  componentId,
  contentId,
  children,
}: CanvasHostHeaderProps) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="flex-end"
    spacing={0.25}
    sx={{ px: 1, pt: 0.5, flexShrink: 0 }}
  >
    {children}
    <CanvasGroupLinkButton componentId={componentId} contentId={contentId} />
  </Stack>
);

export interface CanvasHostShellProps {
  componentId: number;
  contentId: CanvasContentId;
  headerExtras?: ReactNode;
  children: ReactNode;
}

/**
 * Standard shell for Canvas host wrappers — full-height column with the
 * `<CanvasHostHeader />` pinned on top and the body free to fill the
 * remaining space. Use `flex: 1` + `minHeight: 0` on the body so the
 * underlying chart can size itself against a real bounded container.
 */
export const CanvasHostShell = ({
  componentId,
  contentId,
  headerExtras,
  children,
}: CanvasHostShellProps) => (
  <Stack direction="column" sx={{ height: '100%', width: '100%' }}>
    <CanvasHostHeader componentId={componentId} contentId={contentId}>
      {headerExtras}
    </CanvasHostHeader>
    <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>{children}</Box>
  </Stack>
);
